import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Award, Users, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { CATEGORIES, HERO_IMAGE } from "../lib/brand";
import { ProductCard } from "../components/ProductCard";

const METRICS = [
  { icon: TrendingUp, value: "99.4%", label: "Tasa de aprobación" },
  { icon: Users, value: "+12,000", label: "Profesionales acreditados" },
  { icon: Award, value: "SEP · CONOCER", label: "Respaldo oficial" },
];

export default function Home() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("todos");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "todos") params.category = CATEGORIES.find((c) => c.key === category)?.value;
    if (search) params.search = search;
    api
      .get("/products", { params })
      .then((res) => setProducts(res.data))
      .finally(() => setLoading(false));
  }, [category, search]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden brand-navy text-white">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 brand-gradient-v opacity-90" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:py-32">
          <div className="max-w-3xl fade-up">
            <span className="eyebrow mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-emerald-300">
              <ShieldCheck className="h-4 w-4" /> Certificaciones con validez oficial
            </span>
            <h1 className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Tu competencia laboral,
              <span className="text-emerald-400"> oficialmente certificada</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
              Talleres, cursos, diplomados y masters diseñados para acreditar tus habilidades y
              acelerar tu carrera profesional en México.
            </p>

            <div className="mt-8 flex max-w-xl items-center gap-2 rounded-2xl bg-white p-2 shadow-xl">
              <Search className="ml-2 h-5 w-5 flex-none text-slate-400" />
              <input
                data-testid="hero-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Busca una certificación, sector o estándar EC…"
                className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm text-slate-800 outline-none"
              />
              <button
                onClick={() => document.getElementById("catalogo")?.scrollIntoView({ behavior: "smooth" })}
                className="flex-none rounded-xl brand-forest px-5 py-2.5 text-sm font-bold text-white"
              >
                Explorar
              </button>
            </div>

            <div className="mt-12 grid max-w-2xl grid-cols-3 gap-4">
              {METRICS.map((m) => (
                <div key={m.label} className="rounded-2xl bg-white/5 p-4 backdrop-blur-sm">
                  <m.icon className="mb-2 h-5 w-5 text-emerald-400" />
                  <p className="font-display text-lg font-extrabold sm:text-2xl">{m.value}</p>
                  <p className="text-xs text-slate-300">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section id="catalogo" className="mx-auto max-w-7xl px-6 py-16 sm:py-24">
        <div className="mb-8 text-center">
          <span className="eyebrow text-brand-forest">Catálogo de certificaciones</span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">
            Encuentra tu próximo programa
          </h2>
        </div>

        <div className="mb-10 flex flex-wrap justify-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={c.testid}
              onClick={() => setCategory(c.key)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                category === c.key
                  ? "brand-navy text-white shadow-lg"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-brand-navy"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400">Cargando certificaciones…</div>
        ) : products.length === 0 ? (
          <div className="py-20 text-center text-slate-400">No se encontraron programas.</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} featured={p.featured && i === 0 && category === "todos"} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
